import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';

const GlossaryTermHighlight = ({ term, children, isDark }) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: true }); // true = above
  const spanRef = useRef(null);
  const tooltipRef = useRef(null);
  const audioRef = useRef(null);
  const hideTimer = useRef(null);

  const show = useCallback(() => {
    clearTimeout(hideTimer?.current);
    setVisible(true);
  }, []);

  const hide = useCallback(() => {
    hideTimer.current = setTimeout(() => setVisible(false), 120);
  }, []);

  const keepOpen = useCallback(() => {
    clearTimeout(hideTimer?.current);
  }, []);

  // Reposition tooltip if it would overflow viewport
  useEffect(() => {
    if (!visible || !spanRef?.current || !tooltipRef?.current) return;
    const spanRect = spanRef?.current?.getBoundingClientRect();
    const tooltipRect = tooltipRef?.current?.getBoundingClientRect();
    // If not enough space above, show below
    const spaceAbove = spanRect?.top;
    setPosition({ top: spaceAbove >= tooltipRect?.height + 12 });
  }, [visible]);

  const handleAudio = (e) => {
    e?.stopPropagation();
    if (term?.audioUrl) {
      if (!audioRef?.current) {
        audioRef.current = new Audio(term.audioUrl);
      }
      audioRef.current.currentTime = 0;
      audioRef?.current?.play()?.catch(() => {});
    } else if (term?.pronunciation && 'speechSynthesis' in window) {
      const utt = new SpeechSynthesisUtterance(term.word);
      utt.rate = 0.85;
      window.speechSynthesis?.cancel();
      window.speechSynthesis?.speak(utt);
    }
  };

  return (
    <span className="relative inline" ref={spanRef}>
      <span
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        tabIndex={0}
        role="button"
        aria-label={`Glossary term: ${term?.word}`}
        className={`
          cursor-help border-b-2 border-dotted transition-colors duration-150
          ${isDark
            ? 'border-amber-400/70 text-amber-200 hover:border-amber-300 hover:text-amber-100' :'border-amber-500/60 text-amber-800 hover:border-amber-600 hover:text-amber-900'
          }
        `}
        style={{ textDecorationSkipInk: 'none' }}
      >
        {children}
      </span>
      {visible && (
        <span
          ref={tooltipRef}
          onMouseEnter={keepOpen}
          onMouseLeave={hide}
          role="tooltip"
          className={`
            absolute z-50 w-64 rounded-xl shadow-xl border text-left
            pointer-events-auto select-none
            ${position?.top ? 'bottom-full mb-2' : 'top-full mt-2'}
            left-1/2 -translate-x-1/2
            ${isDark
              ? 'bg-gray-900 border-amber-700/60 text-gray-100' :'bg-white border-amber-200 text-gray-900'
            }
          `}
          style={{ minWidth: '220px', maxWidth: '280px' }}
        >
          {/* Arrow */}
          <span
            className={`
              absolute left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 border
              ${position?.top ? '-bottom-1.5 border-t-0 border-l-0' : '-top-1.5 border-b-0 border-r-0'}
              ${isDark ? 'bg-gray-900 border-amber-700/60' : 'bg-white border-amber-200'}
            `}
          />

          <div className="p-3 space-y-2">
            {/* Term + audio button */}
            <div className="flex items-center justify-between gap-2">
              <span className={`font-bold text-sm ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                {term?.word}
              </span>
              {(term?.audioUrl || term?.pronunciation) && (
                <button
                  onClick={handleAudio}
                  title="Hear pronunciation"
                  className={`
                    flex-shrink-0 p-1 rounded-full transition-colors duration-150
                    ${isDark
                      ? 'bg-amber-800/50 hover:bg-amber-700/70 text-amber-300' :'bg-amber-100 hover:bg-amber-200 text-amber-700'
                    }
                  `}
                >
                  <Icon name="Volume2" size={13} />
                </button>
              )}
            </div>

            {/* Pronunciation */}
            {term?.pronunciation && (
              <p className={`text-xs font-mono tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                /{term?.pronunciation}/
              </p>
            )}

            {/* Definition */}
            {term?.definition && (
              <p className={`text-xs leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {term?.definition}
              </p>
            )}
          </div>
        </span>
      )}
    </span>
  );
};

export default GlossaryTermHighlight;
